import { Alert, Col, Progress, Row, TableColumnType, Typography } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { Card } from 'src/components/Card/Card';
import { DataView } from 'src/components/DataView/DataView';
import { Table } from 'src/components/Table/Table';
import { LoanApplicationDto, Transaction } from 'src/generated-api-client';
import { ApplicationsStore } from 'src/stores/ApplicationsStore/ApplicationsStore';
import { ApplicationStatus } from 'src/stores/ApplicationsStore/ApplicationsStore.types';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

export const ApplicationDetailsRepaymentsTab = observer(() => {
    const application =
        ApplicationsStore.currentItem as LoanApplicationDto | null;

    useEffect(() => {
        if (application?.id) {
            ApplicationsStore.loadTransactions(application.id);
        }

        return () => {
            ApplicationsStore.transactionsLoader.reset();
        };
    }, [application?.id]);

    const showRepaymentsInfo =
        application?.status === ApplicationStatus.ACTIVE ||
        application?.status === ApplicationStatus.FULLY_PAID;

    const hasTransactions = ApplicationsStore.transactions.length > 0;

    if (!showRepaymentsInfo) {
        return (
            <Alert
                message={t(
                    'Page.ApplicationDetailsPage.RepaymentsTab.NoRepaymentsInfo',
                    'No repayments registered',
                )}
                type="info"
            />
        );
    }

    return (
        <>
            <Card
                title={t(
                    'Page.ApplicationDetailsPage.RepaymentsTab.RepaymentsProgress.Title',
                    'Repayment Progress',
                )}
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24}>
                        <StyledPercentValue level={4}>
                            {application?.aggregatingFigures
                                ?.repaidPercentage ?? 0}
                            %<br />
                            {t(
                                'Page.ApplicationDetailsPage.RepaymentsTab.RepaymentsProgress.Value',
                                'of loan repaid',
                                {
                                    percentage:
                                        application?.aggregatingFigures
                                            ?.repaidPercentage,
                                },
                            )}
                        </StyledPercentValue>
                        <Progress
                            percent={
                                application?.aggregatingFigures
                                    ?.repaidPercentage
                            }
                            showInfo={false}
                        />
                    </Col>
                    <Col xs={24} sm={12}>
                        <DataView
                            label={t(
                                'Page.ApplicationDetailsPage.RepaymentsTab.RepaymentsProgress.RepaidAmount',
                                'Repaid amount',
                            )}
                            value={
                                application?.aggregatingFigures?.repaidAmount
                            }
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12}>
                        <StyledDataView
                            label={t(
                                'Page.ApplicationDetailsPage.RepaymentsTab.RepaymentsProgress.OutstandingAmount',
                                'Outstanding amount',
                            )}
                            value={
                                application?.aggregatingFigures
                                    ?.outstandingAmount
                            }
                            formatValue="currency-sar"
                        />
                    </Col>
                </Row>
            </Card>

            <Card
                title={t(
                    'Page.ApplicationDetailsPage.RepaymentsTab.Transactions.Title',
                    'Repayment Transactions',
                )}
            >
                {hasTransactions ? (
                    <StyledTable
                        dataSource={ApplicationsStore.transactions}
                        columns={columns}
                    />
                ) : (
                    <Alert
                        message={t(
                            'Page.ApplicationDetailsPage.RepaymentsTab.Transactions.NoTransactionsInfo',
                            'No transactions registered',
                        )}
                    />
                )}
            </Card>
        </>
    );
});

const formNames = FormHelper.createNameProvider<Transaction>();

const columns: TableColumnType<Transaction>[] = [
    {
        key: 'createdAt',
        dataIndex: formNames.getPath('createdAt'),
        title: (
            <Trans i18nKey="Page.ApplicationDetailsPage.RepaymentsTab.Transactions.CreatedAt" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="dateTime" />;
        },
    },
    {
        key: 'date',
        dataIndex: formNames.getPath('date'),
        title: (
            <Trans i18nKey="Page.ApplicationDetailsPage.RepaymentsTab.Transactions.TransactionDate" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="dateTime" />;
        },
    },
    {
        key: 'id',
        dataIndex: formNames.getPath('number'),
        title: (
            <Trans i18nKey="Page.ApplicationDetailsPage.RepaymentsTab.Transactions.TransactionId" />
        ),
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'deductedAmount',
        dataIndex: formNames.getPath('deductedAmount'),
        title: (
            <Trans i18nKey="Page.ApplicationDetailsPage.RepaymentsTab.Transactions.AmountDeducted" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
];

const StyledPercentValue = styled(Typography.Title)`
    font-size: 24px;
    font-weight: 600;
    text-align: center;
`;

const StyledTable = styled(Table)`
    .ant-table-content {
        overflow-x: auto;
    }

    .ant-table-thead > tr > th {
        white-space: nowrap;
    }

    .ant-table-tbody > tr > td {
        white-space: nowrap;
    }

    .ant-pagination-options {
        width: auto !important;
    }
`;

const StyledDataView = styled(DataView)`
    .data-view__label {
        text-align: right;
    }

    .data-view__value {
        text-align: right;
    }
`;
